// src/components/TokenBar/TokenBar.js
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Contextos
import { useSessionTimer } from '../../contexts/SessionTimerContext';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

// Estilos
import styles from './tokenbar.module.css';

function formatRemainingShort(timeLeft, translate) {
  if (timeLeft == null) return '';
  if (timeLeft < 60) return translate('session.seconds', { count: Math.max(0, timeLeft) });
  const mins = Math.floor(timeLeft / 60);
  return translate('session.minutes', { count: mins });
}

function formatRemainingSentence(timeLeft, translate) {
  if (timeLeft == null) return '';
  return translate('session.approximately', {
    time: formatRemainingShort(timeLeft, translate),
  });
}

export default function TokenBar() {
  const { percent, progress, timeLeft, timeTotal, renewNow } = useSessionTimer();
  const { isAuthenticated, tipoUsuario } = useAuth();
  const { showModal } = useModal();
  const { t, i18n } = useTranslation();
  const language = tipoUsuario === 'participante' ? i18n.resolvedLanguage : 'pt-BR';
  const translate = (key, options = {}) => t(key, { ...options, lng: language });

  const safePercent = typeof percent === 'number' ? percent : 0;

  const widthStyle = useMemo(() => {
    return { width: `${safePercent}%` };
  }, [safePercent]);

  // Cores por limiar ( #00AFA9 >40, #A86CF7 >20, #EA1D75 ≤20 )
  const colorFill =
    safePercent > 40 ? '#00AFA9' : safePercent > 20 ? '#A86CF7' : '#EA1D75';

  const timeText =
    timeLeft != null && timeTotal != null
      ? translate('session.remaining', { time: formatRemainingSentence(timeLeft, translate) })
      : translate('session.active');

  if (!isAuthenticated || progress == null) return null;

  const handleClickBar = () => {
    const short = formatRemainingShort(timeLeft, translate);
    const body =
      timeLeft != null
        ? (timeLeft > 60
            ? translate('session.expiresSoon', { time: short })
            : translate('session.expiresVerySoon'))
        : translate('session.renewNow');

    showModal(
      translate('session.renewTitle'),
      body,
      async () => {
        await renewNow();
      },
      () => {
        // nada ao cancelar
      }
    );
  };

  return (
    <div className={styles.container} aria-hidden={!isAuthenticated}>
      <div
        className={styles.track}
        onClick={handleClickBar}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClickBar();
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div
          className={styles.fill}
          style={{ ...widthStyle, background: colorFill }}
          aria-label={translate('session.tokenTimeLeft')}
        />
        <div className={styles.timeLabel}>
          {timeText}
        </div>
      </div>
    </div>
  );
}
